import {
  Component,
  Input,
  Output,
  OnDestroy,
  EventEmitter,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { RewardService } from 'src/app/core/api/reward/reward.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { TimeLineItem } from 'src/app/shared/models/timeline-item/timeline-item';
import { ConsolidatedTransaction } from 'src/app/shared/models/transaction/consolidated-transaction';
import { TransactionGroup } from 'src/app/shared/models/transaction/transaction-group';
import { TransactionType } from 'src/app/shared/models/transaction/transaction-type';

@Component({
  selector: 'app-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
})

/**
 * Displays a timeline of month-expansion-panels
 */
export class TimelineComponent implements OnDestroy {
  @Input() businessId?: number;
  @Output() addedPoint = new EventEmitter<number>();
  @Output() expiringPoints = new EventEmitter<number>();
  timelineItems?: TimeLineItem[] = [];
  months: { [key0: number]: { [key0: number]: TimeLineItem } } = {};
  days: { [key0: number]: { [key0: number]: TimeLineItem } } = {};
  private page = 0;
  private pageSize = 10;
  loading = false;
  private subsink = new Subscription();
  private _filters: any = {};
  private apiCall?: Subscription;

  /**
   * sets filters to some value
   * @param {any} value
   */
  @Input()
  set filters(value: any) {
    this._filters = value;
    this.resetTimeline();
    this.fetchTransactions();
  }

  /**
   * gets filters
   */
  get filters() {
    return this._filters;
  }

  /**
   * @constructor
   */
  constructor(
    public readonly rewardService: RewardService,
    public readonly translationService: TranslationService
  ) {}

  /**
   * cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Pull more transactions
   */
  onScroll() {
    this.fetchTransactions();
  }

  /**
   * Pull paginated transactions
   */
  fetchTransactions() {
    if (!this._filters.startDate || !this._filters.series?.length) return;
    this.apiCall?.unsubscribe();
    this.loading = true;

    let totalAdded = 0;
    let totalExpiring = 0;

    const params = {
      page: this.page,
      pageSize: this.pageSize,
    };

    this.assignParams(params);

    const transactions$ = this.rewardService
      .getTransactions(this.businessId!, params)
      .subscribe((groups) => {
        if (groups.length > 0) {
          this.page++;
        }
        groups.forEach((group) => {
          const day = group.date.getDate();
          const month = group.date.getMonth();

          this.days[month] = this.days[month] || {};
          if (this.days[month][day] == null) {
            this.days[month][day] = new TimeLineItem(group.date, []);
            this.timelineItems!.push(this.days[month][day]);
          }

          const consolidatedList = this.convertGroupToConsolidated(group);
          consolidatedList.forEach((consolidated) => {
            this.days[month][day].transactions.push(consolidated);
            if (
              consolidated.transactionType === TransactionType.ADD ||
              consolidated.transactionType === TransactionType.NEW_EQUIPMENT ||
              consolidated.transactionType === TransactionType.PORT ||
              consolidated.transactionType === TransactionType.REFUND
            ) {
              totalAdded += consolidated.totalPoints;
            } else if (
              consolidated.transactionType === TransactionType.EXPIRE
            ) {
              totalExpiring += consolidated.totalPoints;
            }
          });
        });
        this.addedPoint.emit(totalAdded);
        this.expiringPoints.emit(totalExpiring);
      });
    transactions$.add(() => {
      this.loading = false;
    });
    this.apiCall = transactions$;
    this.subsink.add(transactions$);
  }

  /**
   * Resets timeline
   */
  resetTimeline() {
    this.timelineItems = [];
    this.page = 0;
    this.days = {};
    this.addedPoint.emit(0);
    this.expiringPoints.emit(0);
  }

  /**
   * Assign filter params to be fetched
   * @param {{}} params
   */
  assignParams(params: any) {
    if (this._filters.startDate) {
      params.startDate = this._filters.startDate;
      params.endDate = this._filters.endDate;
    }

    if (this._filters.series && this._filters.series.length > 0) {
      params.familyId = this._filters.series[0];
    }

    if (this._filters.added) params.added = true;
    if (this._filters.spend) params.spend = true;
    if (this._filters.expired) params.expired = true;
  }

  /**
   * Convert Transaction group to consolidated
   * @param {TransactionGroup[]} group
   * @return {ConsolidatedTransaction[]}
   */
  private convertGroupToConsolidated(
    group: TransactionGroup
  ): ConsolidatedTransaction[] {
    const grouped: { [key: string]: ConsolidatedTransaction } = {};

    group.transactions.forEach((transaction) => {
      const key = transaction.transactionType;
      if (!grouped[key]) {
        grouped[key] = new ConsolidatedTransaction(
          transaction.id,
          transaction.totalPoints,
          transaction.transactionType,
          group.date,
          transaction.expirationDate,
          transaction.userName,
          transaction.transactionId,
          transaction.note,
          [transaction]
        );
      } else {
        if (transaction.expirationDate! < grouped[key].expirationDate!) {
          grouped[key].expirationDate = transaction.expirationDate;
        }
        grouped[key].totalPoints += transaction.totalPoints;
        grouped[key].transactions.push(transaction);
      }
    });
    return Object.values(grouped);
  }
}
